import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Form,
  Message,
  Grid,
  Header,
  Segment,
  Checkbox,
  Input,
  Icon,
} from "semantic-ui-react";
import CollegeDepartmentSelector from "./Options/CollegeDepartmentSelector";
import ProgramSelector from "./Options/ProgramSelector";
import YearSelector from "./Options/YearSelector";
import SemesterSelector from "./Options/SemesterSelector";
import { isFacultyUser, isLibrarianOrAdmin } from "UserRoleUtils";

const MESSAGES = {
  success: {
    header: "Request created",
    content: "Your request to loan this literature was created.",
  },
  error: {
    header: "Error",
    fallback: "An error occurred",
  },
};

const FORM_FIELDS = {
  personal: [
    {
      name: "firstName",
      label: "First Name:",
      placeholder: "First Name",
      width: 5,
      mobileWidth: 16,
      required: true,
    },
    {
      name: "lastName",
      label: "Last Name:",
      placeholder: "Last Name",
      width: 5,
      mobileWidth: 16,
      required: true,
    },
    {
      name: "contactNo",
      label: "Contact No.:",
      placeholder: "Contact Number",
      width: 6,
      mobileWidth: 16,
      required: true,
    },
  ],
  dates: [
    {
      name: "checkOutDate",
      label: "Check out Date:",
      type: "date",
      readOnly: false,
      icon: null,
      helpText: null,
    },
    {
      name: "dueDate",
      label: "Due Date:",
      type: "date",
      readOnly: true,
      icon: "info circle",
      helpText:
        "Automatically calculated (3 business days from checkout date).",
    },
  ],
  selectors: [
    {
      component: CollegeDepartmentSelector,
      name: "college",
      label: "College/Department:",
      placeholder: "Select your college or department",
      width: 8,
      mobileWidth: 16,
      required: true,
    },
    {
      component: ProgramSelector,
      name: "program",
      label: "Program:",
      placeholder: "Select your program",
      width: 8,
      mobileWidth: 16,
      required: true,
    },
    {
      component: YearSelector,
      name: "year",
      label: "Year:",
      placeholder: "Select your year level",
      width: 8,
      mobileWidth: 16,
      required: true,
    },
    {
      component: SemesterSelector,
      name: "semester",
      label: "Semester:",
      placeholder: "Select semester",
      width: 8,
      mobileWidth: 16,
      required: true,
    },
  ],
  usage: [
    { value: "home", label: "Home use", field: "homeUse" },
    { value: "library", label: "Library use", field: "libraryUse" },
  ],
};

const checkUserRole = (user) => {
  const isFacultyFromUserType = user?.userType?.some(
    (type) => type.user_type === "Faculty"
  );
  const isLibrarianAdminOrFaculty =
    (user?.roles && (isLibrarianOrAdmin(user) || isFacultyUser(user))) ||
    isFacultyFromUserType;
  const isStudent = user?.userType?.some(
    (type) => type.user_type === "Student"
  );

  return { isLibrarianAdminOrFaculty, isStudent };
};

class SlipForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isMobile: window.innerWidth <= 768,
    };
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  handleResize = () => {
    this.setState({
      isMobile: window.innerWidth <= 768,
    });
  };

  // ✅ Contact number validator
  validateContactNo = (value) => {
    const regex = /^[0-9]{10,15}$/; // only digits, 10–15 length
    return regex.test(value);
  };

  renderMessage = (type, config, error = null) => (
    <Message
      positive={type === "success"}
      negative={type === "error"}
      icon={type === "success" ? "check" : undefined}
      header={config.header}
      content={
        type === "error"
          ? error?.response?.data?.message || config.fallback
          : config.content
      }
    />
  );

  renderPersonalInfoFields = () => {
    const { isMobile } = this.state;
    const { formData } = this.props;

    const errorContact =
      formData.contactNo && !this.validateContactNo(formData.contactNo);

    if (isMobile) {
      return (
        <div style={{ marginBottom: "1rem" }}>
          {FORM_FIELDS.personal.map((field) => (
            <Form.Field
              key={field.name}
              required={field.required}
              error={field.name === "contactNo" && errorContact}
              style={{ marginBottom: "1rem" }}
            >
              <label>{field.label}</label>
              <Input
                name={field.name}
                value={formData[field.name]}
                onChange={this.props.onInputChange}
                placeholder={field.placeholder}
                fluid
                readOnly // ✅ read-only, not disabled
                style={{ backgroundColor: "white", color: "black" }}
              />
              {field.name === "contactNo" && errorContact && (
                <span style={{ color: "red", fontSize: "0.85em" }}>
                  Contact number must be 10–15 digits.
                </span>
              )}
            </Form.Field>
          ))}
        </div>
      );
    }

    return (
      <Grid>
        <Grid.Row>
          {FORM_FIELDS.personal.map((field) => (
            <Grid.Column key={field.name} width={field.width}>
              <Form.Field
                required={field.required}
                error={field.name === "contactNo" && errorContact}
              >
                <label>{field.label}</label>
                <Input
                  name={field.name}
                  value={formData[field.name]}
                  onChange={this.props.onInputChange}
                  placeholder={field.placeholder}
                  type="tel"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  readOnly // ✅ read-only, not disabled
                  style={{ backgroundColor: "white", color: "black" }}
                />
                {field.name === "contactNo" && errorContact && (
                  <span style={{ color: "red", fontSize: "0.85em" }}>
                    Contact number must be 10–15 digits.
                  </span>
                )}
              </Form.Field>
            </Grid.Column>
          ))}
        </Grid.Row>
      </Grid>
    );
  };

  renderSectionHeader = (title) => (
    <Header
      as={this.state.isMobile ? "h5" : "h4"}
      className="slip-form-section"
    >
      {title}
    </Header>
  );

  renderSubSectionHeader = (title) => (
    <div className="slip-form-section-divider">
      <Header
        as={this.state.isMobile ? "h6" : "h5"}
        className="slip-form-subsection"
      >
        {title}
      </Header>
    </div>
  );

  renderSelectorFields = () => {
    const { formData, onDropdownChange, user } = this.props;
    const { isMobile } = this.state;
    const selectors = FORM_FIELDS.selectors;
    const { isStudent } = checkUserRole(user);
    const filteredSelectors = isStudent
      ? selectors
      : selectors.filter(
          (selector) => selector.name !== "year" && selector.name !== "semester"
        );

    if (isMobile) {
      return (
        <div style={{ marginBottom: "1rem" }}>
          {filteredSelectors.map((selector) => {
            const SelectorComponent = selector.component;
            return (
              <div key={selector.name} style={{ marginBottom: "1rem" }}>
                <SelectorComponent
                  name={selector.name}
                  label={selector.label}
                  placeholder={selector.placeholder}
                  value={formData[selector.name]}
                  onChange={onDropdownChange}
                  required={selector.required}
                  fluid
                  disabled={selector.name !== "department"} // disable all except department
                  style={{
                    backgroundColor: "white",
                    color: "black",
                    cursor:
                      selector.name !== "department"
                        ? "not-allowed"
                        : "pointer",
                  }}
                />
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <Grid>
        <Grid.Row>
          {filteredSelectors.slice(0, 2).map((selector) => {
            const SelectorComponent = selector.component;
            return (
              <Grid.Column key={selector.name} width={selector.width}>
                <SelectorComponent
                  name={selector.name}
                  label={selector.label}
                  placeholder={selector.placeholder}
                  value={formData[selector.name]}
                  onChange={onDropdownChange}
                  required={selector.required}
                  disabled={selector.name !== "department"} // disable all except department
                  style={{
                    backgroundColor: "white",
                    color: "black",
                    cursor:
                      selector.name !== "department"
                        ? "not-allowed"
                        : "pointer",
                  }}
                />
              </Grid.Column>
            );
          })}
        </Grid.Row>
        {filteredSelectors.length > 2 && (
          <Grid.Row>
            {filteredSelectors.slice(2).map((selector) => {
              const SelectorComponent = selector.component;
              return (
                <Grid.Column key={selector.name} width={selector.width}>
                  <SelectorComponent
                    name={selector.name}
                    label={selector.label}
                    placeholder={selector.placeholder}
                    value={formData[selector.name]}
                    onChange={onDropdownChange}
                    required={selector.required}
                    disabled={selector.name !== "department"} // disable all except department
                    style={{
                      backgroundColor: "white",
                      color: "black",
                      cursor:
                        selector.name !== "department"
                          ? "not-allowed"
                          : "pointer",
                    }}
                  />
                </Grid.Column>
              );
            })}
          </Grid.Row>
        )}
      </Grid>
    );
  };

  renderDateFields = () => {
    const { isMobile } = this.state;
    const { formData } = this.props;

    // Determine helpText based on usage type
    const getHelpText = (field) => {
      if (field.name === "dueDate") {
        return formData.libraryUse
          ? "Return the same day"
          : "Automatically calculated (3 business days from checkout date).";
      }
      return field.helpText;
    };

    return (
      <div style={{ marginBottom: isMobile ? "1rem" : "0" }}>
        {FORM_FIELDS.dates.map((field) => {
          const helpText = getHelpText(field);
          return (
            <Form.Field
              key={field.name}
              style={{ marginBottom: isMobile ? "1rem" : "1em" }}
            >
              <label>
                {field.label}
                {field.icon && (
                  <Icon
                    name={field.icon}
                    style={{
                      marginLeft: "5px",
                      color: "#2185d0",
                      cursor: "pointer",
                    }}
                    title={helpText}
                  />
                )}
              </label>
              <Input
                type={field.type}
                name={field.name}
                value={formData[field.name]}
                onChange={this.props.onInputChange}
                readOnly={field.name !== "checkOutDate"}
                fluid={isMobile}
                style={{ backgroundColor: "white", color: "black" }}
                title={field.readOnly ? helpText : ""}
              />
            </Form.Field>
          );
        })}
      </div>
    );
  };

  renderUsageOptions = () => {
    const { formData, onUsageChange } = this.props;
    const { isMobile } = this.state;

    return (
      <Form.Field required>
        <div
          className={`slip-form-usage-container ${
            isMobile ? "mobile-usage" : ""
          }`}
        >
          {FORM_FIELDS.usage.map((option) => (
            <div
              key={option.value}
              style={{ marginBottom: isMobile ? "0.5rem" : "0" }}
            >
              <Checkbox
                radio
                name="usageType"
                value={option.value}
                label={option.label}
                checked={formData[option.field] === true}
                onChange={() => onUsageChange(option.value)}
              />
            </div>
          ))}
        </div>
      </Form.Field>
    );
  };

  renderBorrowerInfo = () => {
    const { isMobile } = this.state;

    return (
      <Grid.Column width={isMobile ? 16 : 11}>
        <Segment
          className="slip-form-info"
          style={{ margin: isMobile ? "0 0 1rem 0" : "0" }}
        >
          {this.renderSectionHeader("Borrower's Information")}
          {this.renderPersonalInfoFields()}
          {this.renderSubSectionHeader("Department")}
          {this.renderSelectorFields()}
        </Segment>
      </Grid.Column>
    );
  };

  renderDateAndUsage = () => {
    const { isMobile } = this.state;

    return (
      <Grid.Column width={isMobile ? 16 : 5}>
        <Segment className="slip-form-info">
          {this.renderSectionHeader("Date")}
          {this.renderDateFields()}
          {this.renderSubSectionHeader("Usage *")}
          {this.renderUsageOptions()}
        </Segment>
      </Grid.Column>
    );
  };

  renderBookTitle = () => {
    const { isMobile } = this.state;

    return (
      <div className="slip-form-book-title-section">
        <div
          className="slip-form-book-title"
          style={{
            fontSize: isMobile ? "1.1rem" : "1.2rem",
            wordBreak: "break-word",
            lineHeight: "1.4",
          }}
        >
          Book Title: {this.props.document.metadata.title}
        </div>
      </div>
    );
  };

  renderMainForm = () => {
    const { isMobile } = this.state;

    return (
      <Segment
        className="slip-form-main"
        style={{
          padding: isMobile ? "1rem" : "1.5rem",
          margin: isMobile ? "0" : "0 0 1rem 0",
        }}
      >
        {this.renderBookTitle()}
        <Form>
          <Grid stackable={isMobile}>
            <Grid.Row>
              {this.renderBorrowerInfo()}
              {this.renderDateAndUsage()}
            </Grid.Row>
          </Grid>
        </Form>
      </Segment>
    );
  };

  render() {
    const { hasError, error, isSuccessful } = this.props;
    const { isMobile } = this.state;

    return (
      <div style={{ padding: isMobile ? "0.5rem" : "0" }}>
        {isSuccessful && this.renderMessage("success", MESSAGES.success)}
        {!isSuccessful && (
          <>
            {hasError && this.renderMessage("error", MESSAGES.error, error)}
            {this.renderMainForm()}
          </>
        )}
        <style>
          {`
            @media (max-width: 768px) {
              .slip-form-usage-container.mobile-usage {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
              }

              .slip-form-book-title {
                text-align: center;
                margin-bottom: 1rem;
              }

              .slip-form-section {
                margin-bottom: 0.5rem !important;
              }

              .slip-form-subsection {
                margin-bottom: 0.5rem !important;
              }
            }
          `}
        </style>
      </div>
    );
  }
}

SlipForm.propTypes = {
  formData: PropTypes.object.isRequired,
  document: PropTypes.object.isRequired,
  hasError: PropTypes.bool.isRequired,
  error: PropTypes.object,
  isSuccessful: PropTypes.bool.isRequired,
  user: PropTypes.object,
  onInputChange: PropTypes.func.isRequired,
  onDropdownChange: PropTypes.func.isRequired,
  onUsageChange: PropTypes.func.isRequired,
};

SlipForm.defaultProps = {
  error: null,
  user: {},
};

export default SlipForm;
